import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Info, Home, Database, Users, BookOpen, ChevronDown, Camera, Search, Heart, BookMarked, Settings } from "lucide-react";
import { investigateLinks as sharedInvestigateLinks, userLinks as sharedUserLinks } from "../config/navigation";

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [mobileInvestigateOpen, setMobileInvestigateOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setInvestigateOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change - defer to avoid lint warning
  const prevPathname = useRef(location.pathname);
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      // Defer state updates to next tick
      queueMicrotask(() => {
        setInvestigateOpen(false);
        setMobileMenuOpen(false);
      });
      prevPathname.current = location.pathname;
    }
  }, [location.pathname]);

  // Use shared config with uppercase labels for nav display
  const investigateLinks = sharedInvestigateLinks.map(link => ({
    ...link,
    label: link.label.toUpperCase()
  }));

  const userLinks = sharedUserLinks.map(link => ({
    ...link,
    label: link.label.toUpperCase()
  }));

  const navLinks = [
    { path: "/", label: "HOME", icon: Home },
    { path: "/repository", label: "REPOSITORY", icon: Database },
    { path: "/contribute", label: "CONTRIBUTE", icon: Users },
    { path: "/sources", label: "SOURCES", icon: BookOpen },
    { path: "/about", label: "ABOUT", icon: Info },
  ];

  const isActive = (path) => location.pathname === path;
  const isInvestigateActive = investigateLinks.some(link => location.pathname === link.path);
  const isUserActive = userLinks.some(link => location.pathname === link.path);

  return (
    <nav className="main-nav" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="nav-logo"
          >
            <span className="font-display text-2xl font-bold text-white tracking-tight">
              FLI
            </span>
            <span className="hidden sm:block font-mono text-sm text-gray-500 uppercase tracking-wider">
              Factual Labelling
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home Link - Secondary */}
            <Link
              to="/"
              className={`font-mono text-xs uppercase tracking-wider px-3 py-2 transition-colors relative ${
                isActive("/")
                  ? "text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
              data-testid="nav-link-home"
            >
              HOME
              {isActive("/") && (
                <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-red-600" />
              )}
            </Link>

            {/* Investigate Dropdown - PRIMARY CTA */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setInvestigateOpen(!investigateOpen)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setInvestigateOpen(false);
                }}
                className={`
                  font-mono text-sm font-bold uppercase tracking-wider 
                  px-4 py-2 ml-2 mr-2
                  border-2 transition-all duration-200
                  flex items-center gap-2
                  ${isInvestigateActive || investigateOpen
                    ? "bg-red-600 border-red-600 text-white"
                    : "bg-transparent border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                  }
                `}
                data-testid="nav-investigate-dropdown"
                aria-expanded={investigateOpen}
                aria-haspopup="true"
                aria-controls="investigate-menu"
              >
                <Search className="w-4 h-4" />
                INVESTIGATE
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${investigateOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {investigateOpen && (
                <div 
                  id="investigate-menu"
                  role="menu"
                  className="absolute top-full left-0 mt-1 bg-black border border-gray-800 min-w-[220px] z-50 shadow-xl"
                >
                  {investigateLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        role="menuitem"
                        className={`flex items-center gap-3 font-mono text-sm uppercase tracking-wider px-4 py-3 transition-colors ${
                          isActive(link.path)
                            ? "text-white bg-red-600/20 border-l-2 border-red-600"
                            : "text-gray-400 hover:text-white hover:bg-white/5"
                        }`}
                        data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <Icon className="w-4 h-4" />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Separator */}
            <span className="w-px h-4 bg-gray-800 mx-1" />

            {/* Other Nav Links - Secondary */}
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-mono text-xs uppercase tracking-wider px-3 py-2 transition-colors relative ${
                  isActive(link.path)
                    ? "text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-red-600" />
                )}
              </Link>
            ))}

            {/* User Links (Journal & Personalization) */}
            <span className="w-px h-4 bg-gray-800 mx-1" />
            {userLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-mono text-xs uppercase tracking-wider px-3 py-2 transition-colors relative flex items-center gap-1.5 ${
                    isActive(link.path)
                      ? "text-white"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-3 h-3" />
                  {link.path === '/journal' ? 'JOURNAL' : 'SETTINGS'}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-red-600" />
                  )}
                </Link>
              );
            })}

            {/* Donate Button - CTA */}
            <Link
              to="/donate"
              className={`
                ml-2 flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-wider 
                px-3 py-2 border transition-all duration-200
                ${isActive("/donate")
                  ? "bg-red-600 border-red-600 text-white"
                  : "border-red-600/50 text-red-500 hover:bg-red-600 hover:border-red-600 hover:text-white"
                }
              `}
              data-testid="nav-donate"
            >
              <Heart className="w-3 h-3" />
              DONATE
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden border-t border-gray-800 py-4"
            data-testid="mobile-menu"
          >
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 font-mono text-base uppercase tracking-wider px-4 py-4 transition-colors ${
                isActive("/")
                  ? "text-white bg-white/5 font-medium"
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid="mobile-nav-link-home"
            >
              <Home className="w-5 h-5" />
              HOME
              {isActive("/") && (
                <span className="ml-auto w-2 h-2 bg-red-600" />
              )}
            </Link>

            {/* Investigate Section - Click-based Accordion */}
            <div className="border-t border-gray-800 mt-2 pt-2">
              <button
                onClick={() => setMobileInvestigateOpen(!mobileInvestigateOpen)}
                className={`w-full flex items-center justify-between font-mono text-base uppercase tracking-wider px-4 py-4 transition-colors ${
                  isInvestigateActive
                    ? "text-white bg-white/5 font-medium"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
                aria-expanded={mobileInvestigateOpen}
                aria-controls="mobile-investigate-menu"
              >
                <span className="flex items-center gap-3">
                  <Search className="w-5 h-5" />
                  INVESTIGATE
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileInvestigateOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileInvestigateOpen && (
                <div id="mobile-investigate-menu" className="bg-gray-900/50">
                  {investigateLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 font-mono text-base uppercase tracking-wider px-4 py-4 pl-12 transition-colors ${
                          isActive(link.path)
                            ? "text-white bg-white/10 font-medium"
                            : "text-gray-400 hover:text-white hover:bg-white/5"
                        }`}
                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <Icon className="w-5 h-5" />
                        {link.label}
                        {isActive(link.path) && (
                          <span className="ml-auto w-2 h-2 bg-red-600" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Other Links */}
            <div className="border-t border-gray-800 mt-2 pt-2">
              {navLinks.slice(1).map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 font-mono text-base uppercase tracking-wider px-4 py-4 transition-colors ${
                      isActive(link.path)
                        ? "text-white bg-white/5 font-medium"
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    <Icon className="w-5 h-5" />
                    {link.label}
                    {isActive(link.path) && (
                      <span className="ml-auto w-2 h-2 bg-red-600" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* User Links (Journal & Personalization) */}
            <div className="border-t border-gray-800 mt-2 pt-2">
              <div className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-gray-600">
                Your Data
              </div>
              {userLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 font-mono text-base uppercase tracking-wider px-4 py-4 transition-colors ${
                      isActive(link.path)
                        ? "text-white bg-white/5 font-medium"
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    {link.label}
                    {isActive(link.path) && (
                      <span className="ml-auto w-2 h-2 bg-red-600" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Donate Button - Mobile */}
            <div className="border-t border-gray-800 mt-2 pt-4 px-4 pb-4">
              <Link
                to="/donate"
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center justify-center gap-2 w-full
                  font-mono text-base font-bold uppercase tracking-wider 
                  px-4 py-4 border-2 transition-all duration-200
                  ${isActive("/donate")
                    ? "bg-red-600 border-red-600 text-white"
                    : "border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                  }
                `}
                data-testid="mobile-nav-donate"
              >
                <Heart className="w-5 h-5" />
                SUPPORT FLI
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
